"use client"

import { useCallback } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { BILLING_PERMISSIONS } from "@/lib/auth/permissions.constants"
import type { BillingOverviewResponse } from "@/lib/billing/types"

export const billingOverviewQueryPrefix = ["billing", "overview"] as const

export const billingOverviewQueryKey = (tenantId: string | null) =>
  [...billingOverviewQueryPrefix, tenantId] as const

export async function fetchBillingOverview(
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>,
): Promise<BillingOverviewResponse> {
  const response = await fetchWithAuth("/api/billing/overview")
  if (!response.ok) throw new Error("Failed to load billing overview")
  return response.json() as Promise<BillingOverviewResponse>
}

/** The one client-side entry for the tenant billing snapshot. */
export function useBillingOverviewQuery({ refetchInterval = false }: { refetchInterval?: number | false } = {}) {
  const { fetchWithAuth, isLoading: authLoading } = useAuth()
  const { can, activeTenantId } = usePermissions()
  const queryClient = useQueryClient()
  const canView = can(BILLING_PERMISSIONS.VIEW)

  const query = useQuery({
    queryKey: billingOverviewQueryKey(activeTenantId),
    enabled: Boolean(activeTenantId) && canView,
    queryFn: () => fetchBillingOverview(fetchWithAuth),
    refetchInterval,
  })

  // By prefix, not the tenant-scoped key: a mount-time capture freezes the null tenant id
  // of the auth-bootstrap frame, and key matching is element-wise.
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: billingOverviewQueryPrefix }),
    [queryClient],
  )

  return {
    // `enabled: false` gates fetching, not reading.
    data: canView ? query.data : undefined,
    // The query reports `isLoading: false` while auth bootstraps.
    isLoading: authLoading || query.isLoading,
    isError: query.isError,
    error: query.error,
    // Only a successful fetch moves it — `invalidateQueries` resolves either way.
    dataUpdatedAt: query.dataUpdatedAt,
    refresh,
  }
}
